export interface SaveBackend {
  save(slot: string, data: string): Promise<void>;
  load(slot: string): Promise<string | null>;
  remove(slot: string): Promise<void>;
}

export class InMemoryBackend implements SaveBackend {
  private readonly store = new Map<string, string>();
  async save(slot: string, data: string): Promise<void> {
    this.store.set(slot, data);
  }
  async load(slot: string): Promise<string | null> {
    return this.store.get(slot) ?? null;
  }
  async remove(slot: string): Promise<void> {
    this.store.delete(slot);
  }
}

export class LocalStorageBackend implements SaveBackend {
  private readonly prefix: string;
  constructor(prefix = 'biomass:') {
    this.prefix = prefix;
  }
  async save(slot: string, data: string): Promise<void> {
    localStorage.setItem(this.prefix + slot, data);
  }
  async load(slot: string): Promise<string | null> {
    return localStorage.getItem(this.prefix + slot);
  }
  async remove(slot: string): Promise<void> {
    localStorage.removeItem(this.prefix + slot);
  }
}

export interface GameSnapshot {
  version: number;
  timestamp: number;
  mass: number;
  brain: number;
  humanBrainConsumed: boolean;
  abilitiesUnlocked: string[];
  speciesCounts: Record<string, number>;
  techPoints: number;
  isHardcore: boolean;
}

export type SnapshotInput = Omit<GameSnapshot, 'version' | 'timestamp'>;

export const CURRENT_SAVE_VERSION = 1;

export class SaveSerializer {
  constructor(
    private readonly backend: SaveBackend,
    private readonly slot: string = 'default',
    private readonly version: number = CURRENT_SAVE_VERSION,
  ) {}

  async snapshot(input: SnapshotInput): Promise<void> {
    const snap: GameSnapshot = {
      version: this.version,
      timestamp: Date.now(),
      ...input,
    };
    await this.backend.save(this.slot, JSON.stringify(snap));
  }

  async restore(): Promise<GameSnapshot | null> {
    const raw = await this.backend.load(this.slot);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as GameSnapshot;
      if (typeof parsed.version !== 'number') return null;
      if (parsed.version > this.version) {
        throw new Error(
          `Save version ${parsed.version} is newer than supported ${this.version}`,
        );
      }
      return parsed;
    } catch {
      return null;
    }
  }

  async clear(): Promise<void> {
    await this.backend.remove(this.slot);
  }
}
